import api from './client';

export interface MutationAllele {
  id: string;
  locusId: string;
  symbol: string;
  name: string;
  isWildType: boolean;
  dominanceRank: number;
  phenotypeDescription?: string;
}

export interface MutationLocus {
  id: string;
  speciesId: string;
  locusSymbol: string;
  locusName: string;
  description?: string;
  illustrationUrl?: string;
  illustrationPhotoId?: string;
  chromosome: string;
  inheritanceMode: string;
  category?: string;
  sortOrder: number;
  linkageGroup?: string;
  linkageRecombinationRate?: number;
  alleles: MutationAllele[];
}

export interface BirdGenotypeEntry {
  locusId: string;
  alleles: string[];
  notes?: string;
}

export interface BirdGenotype {
  id: string;
  birdId: string;
  locusId: string;
  alleles: string[];
  isVisual: boolean;
  isSplit: boolean;
  zygosity: string;
  source: string;
  notes?: string;
  locus?: MutationLocus;
}

export interface OffspringOutcome {
  locusSymbol: string;
  genotype: string;
  phenotype: string;
  zygosity: string;
  probability: number;
  sex: 'MALE' | 'FEMALE' | 'EITHER';
  isVisual: boolean;
  isSplit: boolean;
}

export interface PairPredictionResult {
  maleBirdId?: string;
  femaleBirdId?: string;
  speciesId?: string;
  loci: Array<{
    locusSymbol: string;
    locusName: string;
    inheritanceMode: string;
    outcomes: OffspringOutcome[];
    parentMale?: { genotype: string; phenotype: string };
    parentFemale?: { genotype: string; phenotype: string };
  }>;
  combinedPhenotypes: Array<{ phenotype: string; probability: number }>;
  warnings: string[];
  computedAt: string;
}

export interface EggPredictionSnapshot {
  id: string;
  eggId?: string;
  clutchId?: string;
  breedingPairId?: string;
  maleBirdId: string;
  femaleBirdId: string;
  speciesId?: string;
  triggerEvent: string;
  predictionJson: PairPredictionResult;
  actualPhenotype?: string;
  actualMutationName?: string;
  matched?: boolean;
  comparisonMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export const geneticsApi = {
  getSpeciesLoci: (speciesId: string) =>
    api.get<MutationLocus[]>(`/genetics/species/${speciesId}/loci`).then((r) => r.data),

  seedSpecies: (speciesId: string) =>
    api
      .post<{ lociCreated: number; mutationNames: string[]; hasReferenceSeed: boolean }>(
        `/genetics/species/${speciesId}/seed`,
      )
      .then((r) => r.data),

  getSpeciesSeedInfo: (speciesId: string) =>
    api
      .get<{
        hasReferenceSeed: boolean;
        referenceLocusCount: number;
        referenceLabel?: string;
        lociCount: number;
        mutationNames: string[];
        wildTypePhenotype?: string;
        wildTypeNotes?: string;
        catalogUpdatedAt?: string;
      }>(`/genetics/species/${speciesId}/seed-info`)
      .then((r) => r.data),

  applyWildTypeBaseline: (
    speciesId: string,
    payload: { phenotype: string; notes?: string; syncColorDescription?: boolean },
  ) =>
    api
      .post<{
        wildTypePhenotype: string;
        wildTypeNotes?: string;
        colorDescription: string;
        mutationNames: string[];
      }>(`/genetics/species/${speciesId}/wild-type-baseline`, payload)
      .then((r) => r.data),

  uploadLocusIllustration: (locusId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api
      .post<MutationLocus>(`/genetics/loci/${locusId}/illustration`, form)
      .then((r) => r.data);
  },

  clearLocusIllustration: (locusId: string) =>
    api.delete<MutationLocus>(`/genetics/loci/${locusId}/illustration`).then((r) => r.data),

  getBirdGenotypes: (birdId: string) =>
    api.get<BirdGenotype[]>(`/genetics/birds/${birdId}/genotypes`).then((r) => r.data),

  upsertBirdGenotype: (birdId: string, payload: BirdGenotypeEntry) =>
    api.put<BirdGenotype>(`/genetics/birds/${birdId}/genotypes`, payload).then((r) => r.data),

  syncBirdGenotypes: (
    birdId: string,
    payload: { genotypes: BirdGenotypeEntry[]; source?: string },
  ) =>
    api
      .put<{ genotypes: BirdGenotype[]; summary: { mutationName: string; phenotype: string; genotype: string } }>(
        `/genetics/birds/${birdId}/genotypes/bulk`,
        payload,
      )
      .then((r) => r.data),

  importLegacyGenotypes: (birdId: string) =>
    api
      .post<{
        imported: number;
        locusSymbols: string[];
        summary: { mutationName: string; phenotype: string; genotype: string };
      }>(`/genetics/birds/${birdId}/import-legacy`)
      .then((r) => r.data),

  importGeneticCode: (birdId: string, geneticCode: string) =>
    api
      .post<{
        imported: number;
        locusSymbols: string[];
        unmatchedTokens: string[];
        summary: { mutationName: string; phenotype: string; genotype: string };
      }>(`/genetics/birds/${birdId}/import-genetic-code`, { geneticCode })
      .then((r) => r.data),

  suggestCatalogLoci: (speciesId: string) =>
    api
      .post<{
        speciesLabel: string;
        scientificName?: string;
        wildTypeBaseline: {
          phenotype: string;
          genotypeNote: string;
          confidence: 'high' | 'medium' | 'low';
        };
        suggestions: Array<{
          locusSymbol: string;
          locusName: string;
          chromosome: string;
          inheritanceMode: string;
          category?: string;
          description?: string;
          rationale: string;
          evidenceLevel?: 'high' | 'medium' | 'low';
          alleles: Array<{ symbol: string; name: string; isWildType?: boolean }>;
        }>;
        noKnownMutations: boolean;
        verificationNote: string;
        message?: string;
      }>(`/genetics/species/${speciesId}/suggest-catalog`)
      .then((r) => r.data),

  recordCatalogFeedback: (
    speciesId: string,
    payload: {
      outcome: 'accepted' | 'rejected';
      suggestion: object;
      correction?: object;
    },
  ) =>
    api
      .post<{ recorded: boolean }>(
        `/genetics/species/${speciesId}/catalog-feedback`,
        payload,
      )
      .then((r) => r.data),

  predictPair: (maleBirdId: string, femaleBirdId: string) =>
    api.post<PairPredictionResult>('/genetics/predict/pair', { maleBirdId, femaleBirdId }).then((r) => r.data),

  predictBreedingPair: (pairId: string) =>
    api.get<PairPredictionResult>(`/genetics/predict/breeding-pair/${pairId}`).then((r) => r.data),

  predictClutch: (clutchId: string) =>
    api
      .get<PairPredictionResult & {
        clutchId: string;
        breedingPairId: string;
        eggCount: number;
        eggs: Array<{ eggId: string; eggIndex: number; status: string }>;
      }>(`/genetics/predict/clutch/${clutchId}`)
      .then((r) => r.data),

  getEggPrediction: (eggId: string) =>
    api.get<EggPredictionSnapshot | null>(`/genetics/predict/egg/${eggId}`).then((r) => r.data),

  getClutchSnapshots: (clutchId: string) =>
    api.get<EggPredictionSnapshot[]>(`/genetics/predict/clutch/${clutchId}/snapshots`).then((r) => r.data),

  importLegacyForSpecies: (speciesId: string) =>
    api
      .post<{ birdsProcessed: number; birdsImported: number; totalLociImported: number }>(
        `/genetics/species/${speciesId}/import-legacy-all`,
      )
      .then((r) => r.data),

  createLocus: (
    speciesId: string,
    payload: {
      locusSymbol: string;
      locusName: string;
      description?: string;
      chromosome: string;
      inheritanceMode: string;
      category?: string;
      alleles?: Array<{ symbol: string; name: string; isWildType?: boolean }>;
    },
  ) => api.post<MutationLocus>(`/genetics/species/${speciesId}/loci`, payload).then((r) => r.data),

  deleteLocus: (locusId: string) => api.delete(`/genetics/loci/${locusId}`).then((r) => r.data),

  updateLocus: (
    locusId: string,
    payload: {
      locusName?: string;
      description?: string;
      chromosome?: string;
      inheritanceMode?: string;
      category?: string;
      linkageGroup?: string;
      linkageRecombinationRate?: number;
      sortOrder?: number;
      isActive?: boolean;
    },
  ) => api.patch<MutationLocus>(`/genetics/loci/${locusId}`, payload).then((r) => r.data),

  createAllele: (
    locusId: string,
    payload: { symbol: string; name: string; isWildType?: boolean },
  ) => api.post<MutationAllele>(`/genetics/loci/${locusId}/alleles`, payload).then((r) => r.data),

  updateAllele: (
    alleleId: string,
    payload: { symbol?: string; name?: string; phenotypeDescription?: string },
  ) => api.patch<MutationAllele>(`/genetics/alleles/${alleleId}`, payload).then((r) => r.data),

  deleteAllele: (alleleId: string) => api.delete(`/genetics/alleles/${alleleId}`).then((r) => r.data),

  getMutationInventoryReport: (speciesId: string) =>
    api
      .get<{
        speciesId: string;
        speciesName: string;
        totalBirds: number;
        birdsWithStructuredGenotypes: number;
        loci: Array<{
          locusId: string;
          locusSymbol: string;
          locusName: string;
          wildTypeCount: number;
          visualCount: number;
          splitCount: number;
          hemizygousCount: number;
        }>;
      }>(`/genetics/reports/mutation-inventory/${speciesId}`)
      .then((r) => r.data),

  getBreedingOutcomesReport: (params?: { speciesId?: string; breedingPairId?: string; limit?: number }) => {
    const query: Record<string, string> = {};
    if (params?.speciesId) query.speciesId = params.speciesId;
    if (params?.breedingPairId) query.breedingPairId = params.breedingPairId;
    if (params?.limit != null) query.limit = String(params.limit);
    return api
      .get<{
        totalHatchSnapshots: number;
        bandedWithOutcome: number;
        matchedCount: number;
        unmatchedCount: number;
        matchRate: number;
        records: Array<{
          id: string;
          eggId?: string;
          clutchId?: string;
          breedingPairId?: string;
          maleBandId?: string;
          femaleBandId?: string;
          topPrediction?: string;
          topPredictionProbability?: number;
          actualPhenotype?: string;
          actualMutationName?: string;
          matched?: boolean;
          comparisonMessage?: string;
          hatchedAt: string;
          updatedAt: string;
        }>;
      }>('/genetics/reports/breeding-outcomes', query)
      .then((r) => r.data);
  },

  getBreedingPairPredictionHistory: (pairId: string) =>
    api.get<EggPredictionSnapshot[]>(`/genetics/predict/breeding-pair/${pairId}/history`).then((r) => r.data),

  getBirdListingProfile: (birdId: string) =>
    api
      .get<{
        mutationName: string;
        phenotype: string;
        genotype: string;
        tags: string[];
        hasStructuredGenotypes: boolean;
      }>(`/genetics/birds/${birdId}/listing-profile`)
      .then((r) => r.data),

  getBirdCoi: (birdId: string) =>
    api
      .get<{
        birdId: string;
        coefficient: number;
        percent: number;
        knownAncestors: number;
        message?: string;
      }>(`/genetics/birds/${birdId}/coi`)
      .then((r) => r.data),

  getPairCoi: (maleBirdId: string, femaleBirdId: string) =>
    api
      .get<{
        maleBirdId: string;
        femaleBirdId: string;
        coefficient: number;
        percent: number;
        message?: string;
      }>('/genetics/predict/pair-coi', { maleBirdId, femaleBirdId })
      .then((r) => r.data),

  getTestBreedingGuide: (birdId: string, locusId: string) =>
    api
      .get<{
        applicable: boolean;
        message?: string;
        locusSymbol?: string;
        locusName?: string;
        testMateDescription?: string;
        maleGenotype?: string;
        interpretation?: string;
        expectedOutcomes?: Array<{
          phenotype: string;
          genotype: string;
          probability: number;
          sex: string;
        }>;
      }>(`/genetics/birds/${birdId}/test-breeding/${locusId}`)
      .then((r) => r.data),

  getSplitCarriersReport: (speciesId: string) =>
    api
      .get<{
        speciesId: string;
        speciesName: string;
        totalSplitCarriers: number;
        byLocus: Array<{
          locusId: string;
          locusSymbol: string;
          locusName: string;
          carriers: Array<{ birdId: string; bandId?: string; alleles: string[] }>;
        }>;
      }>(`/genetics/reports/split-carriers/${speciesId}`)
      .then((r) => r.data),

  backfillHatchSnapshots: () =>
    api
      .post<{ eggsProcessed: number; snapshotted: number; skipped: number }>(
        '/genetics/predict/backfill-hatch-snapshots',
      )
      .then((r) => r.data),
};
